/**
 * Cloud productions on the wire (PLAN §9, D10, D11, D13): a production that
 * runs in Fargate, launched from the portal, as a node of the tenant's
 * automatic cloud site (`site.kind = 'cloud'`, §9.1).
 *
 *   - **One record per production.** Launching creates the production (the
 *     ordinary record, `productions.ts`, in a studio of the cloud site) and
 *     this record beside it, which says how it runs: the task size, whether it
 *     is redundant (two tasks in two AZs, §9.3 — off by default, D11), when it
 *     starts, which package it is pinned to, and its tasks.
 *   - **Status** — `pending` (created; a scheduled start waits for its launch
 *     time, T−10 min, §9.2.4), `starting` (tasks placed, the node not ready
 *     yet), `running` (the main node enrolled, opened its site link and said
 *     it is ready), `stopping` (asked to stop: the task drains — the final
 *     as-run, the outbox — and exits), `stopped`, `failed` (it could not be
 *     launched, never became ready, or kept dying: `statusReason` says which).
 *   - **Cost (§9.6).** `cost` is an estimate from the on-demand Fargate price
 *     of the task size (`CLOUD_TASK_SIZES`), per task, from each task's start
 *     to its stop (or now). What is billed is metered in DynamoDB per task run.
 *
 * Cloud only, like the sites table: a node does not launch cloud productions.
 * `.strict()` as the rest of the contract.
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { sealedBoxSchema } from './sites.js';
import { showStyleIdSchema, siteIdSchema, studioIdSchema } from './studios.js';

const isoSchema = z.iso.datetime({ offset: true });
const userRefSchema = z.string().min(1).max(128);

// ─── sizes and pricing (§9.6) ───

export const CLOUD_TASK_SIZE_IDS = ['small', 'medium', 'large'] as const;
export const cloudTaskSizeSchema = z.enum(CLOUD_TASK_SIZE_IDS);
export type CloudTaskSize = z.infer<typeof cloudTaskSizeSchema>;

/**
 * Fargate on-demand, Linux/ARM64 (Graviton — the runtime image is multi-arch),
 * eu-north-1, from the AWS Price List API on 2026-09-26:
 * `EUN1-Fargate-ARM-vCPU-Hours:perCPU` $0.0356, `EUN1-Fargate-ARM-GB-Hours`
 * $0.00392. An estimate for the portal (§9.6), not the invoice.
 */
export const FARGATE_ARM_PRICE_USD = { perVcpuHour: 0.0356, perGbHour: 0.00392 } as const;

/** One task size: what the task definition asks Fargate for (`cpu` units, MiB) and what an hour of it costs. */
export const cloudTaskSizeInfoSchema = z
  .object({
    taskSize: cloudTaskSizeSchema,
    vcpu: z.number().positive(),
    memoryGb: z.number().positive(),
    /** One task for one hour, USD (on-demand). */
    hourlyUsd: z.number().nonnegative(),
  })
  .strict();
export type CloudTaskSizeInfo = z.infer<typeof cloudTaskSizeInfoSchema>;

/** PLAN §9.6: "roughly 0.5–1 vCPU and 1–2 GB is likely enough"; `large` for headroom while we measure. */
const SIZE_SHAPES: Readonly<Record<CloudTaskSize, { vcpu: number; memoryGb: number }>> = {
  small: { vcpu: 0.5, memoryGb: 1 },
  medium: { vcpu: 1, memoryGb: 2 },
  large: { vcpu: 2, memoryGb: 4 },
};

/** Rounded to a millionth of a dollar: exact for these prices, without floating-point dust. */
function usd(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

/** The hourly price of one task of `size`. */
export function taskHourlyUsd(size: CloudTaskSize): number {
  const shape = SIZE_SHAPES[size];
  return usd(shape.vcpu * FARGATE_ARM_PRICE_USD.perVcpuHour + shape.memoryGb * FARGATE_ARM_PRICE_USD.perGbHour);
}

export const CLOUD_TASK_SIZES: readonly CloudTaskSizeInfo[] = CLOUD_TASK_SIZE_IDS.map((taskSize) => ({ taskSize, ...SIZE_SHAPES[taskSize], hourlyUsd: taskHourlyUsd(taskSize) }));

/** The running estimate of a production: one task, or two when redundant (§9.3), per hour. */
export function productionHourlyUsd(size: CloudTaskSize, redundant: boolean): number {
  return usd(taskHourlyUsd(size) * (redundant ? 2 : 1));
}

// ─── the record ───

export const CLOUD_PRODUCTION_STATUSES = ['pending', 'starting', 'running', 'stopping', 'stopped', 'failed'] as const;
export const cloudProductionStatusSchema = z.enum(CLOUD_PRODUCTION_STATUSES);
export type CloudProductionStatus = z.infer<typeof cloudProductionStatusSchema>;

/** `stopped` and `failed`: nothing runs and nothing will. */
export function isTerminalCloudStatus(status: CloudProductionStatus): boolean {
  return status === 'stopped' || status === 'failed';
}

/** A task's role: the main node, or the backup of a redundant production (§9.3). */
export const cloudTaskRoleSchema = z.enum(['main', 'backup']);
export type CloudTaskRole = z.infer<typeof cloudTaskRoleSchema>;

/**
 * One task's lifecycle, simplified from ECS's `lastStatus`: `launching` (the
 * cloud is calling RunTask), `provisioning` / `pending` (Fargate places it and
 * pulls the image), `running`, `stopping`, `stopped`.
 */
export const cloudTaskStatusSchema = z.enum(['launching', 'provisioning', 'pending', 'running', 'stopping', 'stopped']);
export type CloudTaskStatus = z.infer<typeof cloudTaskStatusSchema>;

export const cloudProductionTaskSchema = z
  .object({
    role: cloudTaskRoleSchema,
    /** ECS's task id (the last segment of its ARN), or null before RunTask answered. */
    taskId: z.string().max(64).nullable(),
    status: cloudTaskStatusSchema,
    availabilityZone: z.string().max(32).nullable(),
    /** The task's private VPC address (its "LAN IP", the peer link between a pair, §9.3). */
    privateIp: z.string().max(64).nullable(),
    /** The node the task enrolled as, once it has. */
    nodeId: z.string().max(64).nullable(),
    /** 1 for the first launch of this role; each relaunch after an unexpected stop adds one (§9.2.6). */
    launch: z.number().int().positive(),
    launchedAt: isoSchema,
    startedAt: isoSchema.nullable(),
    stoppedAt: isoSchema.nullable(),
    /** ECS's stop code and reason, or the cloud's, for a stopped task. */
    stopReason: z.string().max(1000).nullable(),
  })
  .strict();
export type CloudProductionTask = z.infer<typeof cloudProductionTaskSchema>;

export const cloudProductionCostSchema = z
  .object({
    /** The production's price per hour while it runs (all its tasks). */
    hourlyUsd: z.number().nonnegative(),
    /** Task-hours so far: every task run, start to stop (or now). */
    taskHours: z.number().nonnegative(),
    /** `taskHours` at the task size's price. */
    accruedUsd: z.number().nonnegative(),
  })
  .strict();

export const cloudProductionSchema = z
  .object({
    productionId: ulidSchema,
    studioId: studioIdSchema,
    /** Always the cloud site (§9.1); carried so a client needs no second lookup. */
    siteId: siteIdSchema,
    playlistId: ulidSchema.nullable(),
    rundownId: ulidSchema.nullable(),
    rehearsal: z.boolean(),
    status: cloudProductionStatusSchema,
    /** Why it is in its status, for `failed`, `stopping` and `stopped` (and a relaunch), else null. */
    statusReason: z.string().max(1000).nullable(),
    redundant: z.boolean(),
    taskSize: cloudTaskSizeSchema,
    /** Runs the simulated devices as a sidecar in the task (§9.5, for demos and tests). */
    simulators: z.boolean(),
    /** When the show starts, or null for "now". */
    startAt: isoSchema.nullable(),
    /** When the task(s) are, or were, launched: `startAt` − 10 min (§9.2.4), or the request's time. */
    launchAt: isoSchema,
    /** The production package it is pinned to (§4.1, D15), resolved at launch. */
    packageHash: z.string().max(128).nullable(),
    tasks: z.array(cloudProductionTaskSchema),
    /** Relaunches after unexpected stops so far (§9.2.6). */
    relaunches: z.number().int().nonnegative(),
    maxDurationMinutes: z.number().int().positive(),
    /** The safety net (§9.2.5): stopped when this passes, whatever it is doing. */
    deadlineAt: isoSchema,
    readyAt: isoSchema.nullable(),
    stopRequestedAt: isoSchema.nullable(),
    stopRequestedBy: userRefSchema.nullable(),
    stoppedAt: isoSchema.nullable(),
    /** The static addresses its traffic leaves AWS from (D11): what a customer allowlists. */
    egressIps: z.array(z.string()),
    cost: cloudProductionCostSchema,
    createdAt: isoSchema,
    createdBy: userRefSchema,
  })
  .strict();
export type CloudProduction = z.infer<typeof cloudProductionSchema>;

// ─── requests and responses ───

/** The longest a cloud production may run (§1.1: "up to 7 days continuous"). */
export const MAX_CLOUD_PRODUCTION_MINUTES = 7 * 24 * 60;
/** The default safety net: 12 hours, set per production at launch. */
export const DEFAULT_CLOUD_PRODUCTION_MINUTES = 12 * 60;
/** How far ahead a start may be scheduled. */
export const MAX_SCHEDULE_AHEAD_DAYS = 30;
/** Scheduled starts launch this long before `startAt` (§9.2.4: "T−10 min by default"). */
export const PRELAUNCH_MINUTES = 10;

export const launchCloudProductionRequestSchema = z
  .object({
    /** Client-chosen ULID: a retried launch returns the production as it is. */
    productionId: ulidSchema.optional(),
    /** A studio of the tenant's cloud site (§9.1). */
    studioId: studioIdSchema,
    /** The show style to run (D32); absent = the studio's `defaultShowStyleId`. */
    showStyleId: showStyleIdSchema.optional(),
    /** Run a playlist… */
    playlistId: ulidSchema.nullable(),
    /** …or a single rundown. Exactly one of the two. */
    rundownId: ulidSchema.nullable(),
    /** `now`, or when the show starts (the task launches `PRELAUNCH_MINUTES` before it). */
    startAt: z.union([z.literal('now'), isoSchema]),
    /** Two tasks in two AZs, main and backup, the cloud as witness (§9.3). Off by default (D11). */
    redundant: z.boolean(),
    taskSize: cloudTaskSizeSchema,
    /** Created for rehearsal (as `createProduction`). Default false. */
    rehearsal: z.boolean().optional(),
    /** Run the three simulated devices as a sidecar in the task (§9.5). Default false. */
    simulators: z.boolean().optional(),
    /** The safety net: the production is stopped after this long (default 12 h, at most 7 days). */
    maxDurationMinutes: z.number().int().min(15).max(MAX_CLOUD_PRODUCTION_MINUTES).optional(),
  })
  .strict()
  .refine((body) => (body.playlistId === null) !== (body.rundownId === null), {
    message: 'set exactly one of playlistId and rundownId',
    path: ['playlistId'],
  });
export type LaunchCloudProductionRequest = z.infer<typeof launchCloudProductionRequestSchema>;

export const stopCloudProductionRequestSchema = z
  .object({
    /** Why, for the record and the as-run log; null for none. */
    reason: z.string().max(500).nullable(),
  })
  .strict();

export const cloudProductionPathSchema = z.object({ productionId: ulidSchema.describe('The production’s id (a ULID).') }).strict();

export const listCloudProductionsQuerySchema = z
  .object({
    status: cloudProductionStatusSchema.optional().describe('Only cloud productions in this status.'),
    active: z.enum(['true', 'false']).optional().describe('`true`: only those not stopped or failed.'),
  })
  .strict();

/** Where cloud productions run and what they cost: the portal's launch dialog shows it (D11, §9.6). */
export const cloudProductionNetworkSchema = z
  .object({
    region: z.string(),
    /** The NAT gateway's Elastic IPs: every task's internet traffic leaves from these (D11). */
    egressIps: z.array(z.string()),
    sizes: z.array(cloudTaskSizeInfoSchema),
  })
  .strict();
export type CloudProductionNetwork = z.infer<typeof cloudProductionNetworkSchema>;

export const cloudProductionResponseSchema = z.object({ cloudProduction: cloudProductionSchema }).strict();
export const listCloudProductionsResponseSchema = z
  .object({
    /** Newest first. */
    cloudProductions: z.array(cloudProductionSchema),
    network: cloudProductionNetworkSchema,
  })
  .strict();

// ─── metering (§9.6) ───

export const usageMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'a month is YYYY-MM');

export const cloudUsageQuerySchema = z.object({ month: usageMonthSchema.optional().describe('The month (UTC), `YYYY-MM`; default the current one.') }).strict();

/** One task run, as metered: from its start to its stop. */
export const cloudUsageRunSchema = z
  .object({
    productionId: ulidSchema,
    role: cloudTaskRoleSchema,
    taskId: z.string(),
    taskSize: cloudTaskSizeSchema,
    startedAt: isoSchema,
    stoppedAt: isoSchema,
    hours: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
  })
  .strict();

export const cloudUsageResponseSchema = z
  .object({
    month: usageMonthSchema,
    /** Production-hours: each production's first start to its stop (a redundant production counts once). */
    productionHours: z.number().nonnegative(),
    /** Task-hours: every task run (a redundant production counts twice). */
    taskHours: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
    runs: z.array(cloudUsageRunSchema),
  })
  .strict();
export type CloudUsageResponse = z.infer<typeof cloudUsageResponseSchema>;

// ─── the task's side (`auth: 'node'`) ───

/**
 * What a cloud production's task is to do (§9.2.3), asked by its supervisor
 * with its node signature: which production, as which role, whether to stop,
 * and — for a redundant pair — its peer and the pair's LAN secret, sealed to
 * the asking node (context `pair-secret:<productionId>:<nodeId>`).
 */
export const cloudAssignmentSchema = z
  .object({
    productionId: ulidSchema,
    role: cloudTaskRoleSchema,
    status: cloudProductionStatusSchema,
    /** Drain and exit (§9.2.5): asked to stop, the production ended, or its deadline passed. */
    stop: z.boolean(),
    stopReason: z.string().max(1000).nullable(),
    /** The other task of a redundant pair, once it has enrolled; null otherwise. */
    peer: z.object({ nodeId: z.string(), lanIp: z.string() }).strict().nullable(),
    /** The pair's secret (`ACP_PEER_SECRET`), sealed to this node; null unless redundant. */
    pairSecret: sealedBoxSchema.nullable(),
  })
  .strict();
export type CloudAssignment = z.infer<typeof cloudAssignmentSchema>;

/** The seal context of the pair secret for one node: a box cannot be replayed for another production or node. */
export function pairSecretContext(productionId: string, nodeId: string): string {
  return `pair-secret:${productionId}:${nodeId}`;
}

export const cloudAssignmentResponseSchema = z.object({ assignment: cloudAssignmentSchema }).strict();

export const cloudReadyRequestSchema = z.object({ productionId: ulidSchema }).strict();
